import {
  BusinessRounded,
  MonitorHeartRounded,
  NumbersRounded,
  StorageRounded,
} from "@mui/icons-material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
  Chip,
  ListItemButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";
import { DOCS_BASE_PATH } from "pages/Docs/docsManifest";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { colorOverrides } from "theme/colorOverrides";
import { MessageContext } from "utils/MessageProvider";
import { OrganizationType, SupportType } from "utils/saastypes";
import AppLogo from "./AppLogo";
import { Header } from "./Header";
import ChatIcon from "./icons/ChatIcon";
import HelpIcon from "./icons/HelpIcon";
import SlackIcon from "./icons/SlackIcon";
import TokenIcon from "./icons/TokenIcon";
import { SidebarTrialCard } from "./SidebarTrialCard";
import { SupportModal } from "./SupportModal/SupportModal";

const STORAGE_DOCS_VISITED_KEY = "docsVisited";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(18)} + 1px)`, // (18 x 4) + 1 = 72px
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(18)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function PostLoginContent() {
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(true);
  const [supportModalActive, setSupportModal] = useState(false);
  const { authService } = useAuth();
  const [
    {
      isOrkesUser,
      currentUser,
      organizationType,
      currentOrganization,
      braavosSubscription,
    },
  ] = useAuthMachine(authService);

  // The Docs "New" chip shows until the user first opens any Docs page.
  const { pathname } = useLocation();
  const [docsVisited, setDocsVisited] = useState(
    () => localStorage.getItem(STORAGE_DOCS_VISITED_KEY) === "true"
  );
  useEffect(() => {
    if (!docsVisited && pathname.startsWith(DOCS_BASE_PATH)) {
      localStorage.setItem(STORAGE_DOCS_VISITED_KEY, "true");
      setDocsVisited(true);
    }
  }, [docsVisited, pathname]);

  useEffect(() => {
    if (isScreenSmall) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isScreenSmall]);

  const toggleLeftMenu = () => {
    setOpen(!open);
  };

  const currentYear = new Date().getFullYear();

  const { setMessage } = useContext(MessageContext);

  const handleCopyToken = () => {
    const token = currentUser?.accessToken
      ? currentUser.accessToken
      : "NO TOKEN";
    navigator.clipboard.writeText(token);
    setMessage({ text: "Copied!", severity: "success" });
  };

  const availableSupportPlan = useMemo(() => {
    if (braavosSubscription) {
      const supportPlan = braavosSubscription?.supportInfo;
      return supportPlan?.length ? supportPlan : [SupportType.BASIC];
    }
  }, [braavosSubscription]);

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={open}
        className={"leftDrawer"}
        sx={{ position: "relative" }}
      >
        <Link to={"/home"} style={{ textDecoration: "none", opacity: 1 }}>
          <AppLogo open={open} />
        </Link>
        <List
          sx={{
            "& a": {
              paddingTop: "15px",
            },
            color: colorOverrides.white,
            "& svg": {
              fontSize: "26px",
              color: colorOverrides.white,
            },
            "& span": {
              fontSize: "15px",
              color: colorOverrides.white,
            },
          }}
        >
          <Tooltip title={open ? "" : "Clusters"} arrow placement="right">
            <ListItemButton
              key={"Clusters"}
              component={Link}
              to={"/home/clusters"}
            >
              <ListItemIcon>
                <StorageRounded />
              </ListItemIcon>
              <ListItemText primary={"Clusters"} />
            </ListItemButton>
          </Tooltip>

          <Tooltip
            title={open ? "" : "Organization Settings"}
            arrow
            placement="right"
          >
            <ListItemButton
              key={"Organization Settings"}
              component={Link}
              to={"/home/organization-setting"}
            >
              <ListItemIcon>
                <BusinessRounded />
              </ListItemIcon>
              <ListItemText primary={"Organization Settings"} />
            </ListItemButton>
          </Tooltip>
          {isOrkesUser && (
            <Tooltip
              title={open ? "" : "Permitted Users"}
              arrow
              placement="right"
            >
              <ListItemButton
                key={"Permitted Users"}
                component={Link}
                to={"/home/permitted/users"}
              >
                <ListItemIcon>
                  <FactCheckIcon />
                </ListItemIcon>
                <ListItemText primary={"Permitted Users"} />
              </ListItemButton>
            </Tooltip>
          )}
          {isOrkesUser && (
            <Tooltip title={open ? "" : "Versions"} arrow placement="right">
              <ListItemButton
                key={"Versions"}
                component={Link}
                to={"/home/versions"}
              >
                <ListItemIcon>
                  <NumbersRounded />
                </ListItemIcon>
                <ListItemText primary={"Versions"} />
              </ListItemButton>
            </Tooltip>
          )}
          {isOrkesUser && (
            <Tooltip
              title={open ? "" : "Mission Control"}
              arrow
              placement="right"
            >
              <ListItemButton
                key={"Mission Control"}
                component={Link}
                to={"/home/mission-control"}
              >
                <ListItemIcon>
                  <MonitorHeartRounded />
                </ListItemIcon>
                <ListItemText primary={"Mission Control"} />
              </ListItemButton>
            </Tooltip>
          )}
        </List>
        <Divider />
        <List
          sx={{
            "& a": {
              paddingTop: "15px",
            },
            color: colorOverrides.white,
            "& svg": {
              fontSize: "26px",
              color: colorOverrides.white,
            },
            "& span": {
              fontSize: "15px",
              color: colorOverrides.white,
            },
          }}
        >
          {organizationType === OrganizationType.ENTERPRISE ||
          organizationType === OrganizationType.FREE_TRIAL ? (
            <Tooltip title={open ? "" : "Support"} arrow placement="right">
              <ListItemButton
                key={"Support"}
                onClick={() => setSupportModal(true)}
                sx={{ pt: "15px" }}
              >
                <ListItemIcon>
                  <ChatIcon size={24} />
                </ListItemIcon>
                <ListItemText primary={"Support"} />
              </ListItemButton>
            </Tooltip>
          ) : null}
          <Tooltip title={open ? "" : "Docs"} arrow placement="right">
            <ListItemButton
              key={"Docs"}
              component={Link}
              to="/home/docs"
              sx={{ pt: "15px" }}
            >
              <ListItemIcon>
                <HelpIcon size={24} />
              </ListItemIcon>
              <ListItemText primary={"Docs"} sx={{ flex: "0 0 auto" }} />
              {open && !docsVisited && (
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    ml: 5,
                    height: 16,
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: colorOverrides.brand07,
                    "& .MuiChip-label": { fontSize: 8 },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
          <Tooltip title={open ? "" : "Slack"} arrow placement="right">
            <ListItemButton
              key={"Slack"}
              component={Link}
              to="https://join.slack.com/t/orkes-conductor/shared_invite/zt-3dpcskdyd-W895bJDm8psAV7viYG3jFA"
              target={"noopener noreferrer"}
              sx={{ pt: "15px" }}
            >
              <ListItemIcon>
                <SlackIcon size={22} />
              </ListItemIcon>
              <ListItemText primary={"Slack"} />
            </ListItemButton>
          </Tooltip>
          {(isOrkesUser ||
            organizationType === OrganizationType.ENTERPRISE) && (
            <Tooltip title={open ? "" : "Copy Token"} arrow placement="right">
              <ListItemButton
                key={"Copy Token"}
                onClick={handleCopyToken}
                sx={{ pt: "15px" }}
              >
                <ListItemIcon>
                  <TokenIcon size={22} />
                </ListItemIcon>
                <ListItemText primary={"Copy Token"} />
              </ListItemButton>
            </Tooltip>
          )}
        </List>

        {open && organizationType === OrganizationType.FREE_TRIAL && (
          <SidebarTrialCard currentOrganization={currentOrganization} />
        )}
      </Drawer>

      <SupportModal
        open={supportModalActive}
        onClose={() => setSupportModal(false)}
        availableSupportPlan={availableSupportPlan}
      />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        style={{
          marginTop: 0,
          padding: 0,
          backgroundColor: "#f9f9f9",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          handleDrawerOpen={toggleLeftMenu}
          organizationName={currentOrganization?.name}
        />
        <div style={{ flexGrow: 1 }}>
          <Outlet />
        </div>
        <footer className="innerFooter">
          © {currentYear} Orkes Inc. - Orkes, Orkes logo are registered
          trademarks of Orkes Inc.
          <a
            target={"_blank"}
            rel={"noreferrer noopener"}
            href="https://orkes.io/terms-and-conditions/"
          >
            Terms and Conditions
          </a>
          <a
            target={"_blank"}
            rel={"noreferrer noopener"}
            href="https://orkes.io/privacy-policy/"
          >
            Privacy Policy
          </a>
          <a
            target={"_blank"}
            rel={"noreferrer noopener"}
            href="https://orkes.io/legal/"
          >
            Legal
          </a>
        </footer>
      </Box>
    </Box>
  );
}
